

import { useState } from 'react';
import { bookingsAPI, discountsAPI } from '../../lib/api';

export default function BookingForm({ tour }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    numberOfPeople: 1,
    message: '',
  });

  const [discountCode, setDiscountCode] = useState('');
  const [discount, setDiscount] = useState(null);
  const [discountError, setDiscountError] = useState('');
  const [checkingDiscount, setCheckingDiscount] = useState(false);

  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [errorMessage, setErrorMessage] = useState('');
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }

    if (formData.numberOfPeople < 1) {
      newErrors.numberOfPeople = 'At least 1 person required';
    } else if (tour.availableSeats && formData.numberOfPeople > tour.availableSeats) {
      newErrors.numberOfPeople = `Only ${tour.availableSeats} seats available`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleApplyDiscount = async () => {
    if (!discountCode.trim()) return;

    setCheckingDiscount(true);
    setDiscountError('');

    try {
      const result = await discountsAPI.apply(discountCode);
      if (result.valid) {
        setDiscount(result);
        setDiscountError('');
      } else {
        setDiscountError('Invalid or expired discount code');
        setDiscount(null);
      }
    } catch (error) {
      setDiscountError('Invalid or expired discount code');
      setDiscount(null);
    } finally {
      setCheckingDiscount(false);
    }
  };

  const calculateTotal = () => {
    const subtotal = tour.price * formData.numberOfPeople;
    
    if (!discount) return subtotal;

    if (discount.type === 'percentage') {
      return subtotal - (subtotal * discount.amount / 100);
    } else if (discount.type === 'fixed') {
      return Math.max(0, subtotal - discount.amount);
    }

    return subtotal;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setStatus('loading');
    setErrorMessage('');

    try {
      const bookingData = {
        tourId: tour.id,
        ...formData,
      };

      await bookingsAPI.create(bookingData);
      setStatus('success');
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        numberOfPeople: 1,
        message: '',
      });
      setDiscount(null);
      setDiscountCode('');
    } catch (error) {
      setStatus('error');
      setErrorMessage(error.message || 'Failed to create booking. Please try again.');
    }
  };

  const subtotal = tour.price * formData.numberOfPeople;
  const total = calculateTotal();
  const savings = subtotal - total;

  if (status === 'success') {
    return (
      <div className="bg-white rounded-xl shadow-lg p-6 lg:p-8">
        <div className="text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-green-600"
             
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">
            Booking Submitted!
          </h3>
          <p className="text-gray-600 mb-6">
            Thank you for your booking request. We'll contact you shortly via email to confirm your reservation.
          </p>
          <button
            onClick={() => setStatus('idle')}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors"
          >
            Make Another Booking
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 lg:p-8">
      <h3 className="text-2xl font-bold text-gray-900 mb-6">Book This Tour</h3>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Full Name *
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            className={`w-full px-4 py-3 border text-gray-500 ${
              errors.name ? 'border-red-500' : 'border-gray-300'
            } rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
            placeholder="John Doe"
          />
          {errors.name && (
            <p className="mt-1 text-sm text-red-600">{errors.name}</p>
          )}
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email Address *
          </label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            className={`w-full px-4 py-3 border text-gray-500 ${
              errors.email ? 'border-red-500' : 'border-gray-300'
            } rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
            placeholder="john@example.com"
          />
          {errors.email && (
            <p className="mt-1 text-sm text-red-600">{errors.email}</p>
          )}
        </div>

        {/* Phone */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Phone Number *
          </label>
          <input
            type="tel"
            value={formData.phone}
            onChange={(e) => handleInputChange('phone', e.target.value)}
            className={`w-full px-4 py-3 border text-gray-500 ${
              errors.phone ? 'border-red-500' : 'border-gray-300'
            } rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
            placeholder="+1 (555) 123-4567"
          />
          {errors.phone && (
            <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
          )}
        </div>

        {/* Number of People */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Number of People *
          </label>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => handleInputChange('numberOfPeople', Math.max(1, formData.numberOfPeople - 1))}
              className="w-10 h-10 flex items-center justify-center text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5" stroke='currentColor' fill="none">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
              </svg>
            </button>
            <input
              type="number"
              value={formData.numberOfPeople}
              onChange={(e) => handleInputChange('numberOfPeople', parseInt(e.target.value) || 1)}
              min="1"
              max={tour.availableSeats || 999}
              className={`flex-1 px-4 py-3 border ${
                errors.numberOfPeople ? 'border-red-500' : 'border-gray-300'
              } rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-500 focus:border-transparent text-center`}
            />
            <button
              type="button"
              onClick={() => handleInputChange('numberOfPeople', formData.numberOfPeople + 1)}
              className="w-10 h-10 flex items-center justify-center bg-gray-200 hover:bg-gray-300 text-gray-500 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5" stroke='currentColor' fill="none">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
          </div>
          {errors.numberOfPeople && (
            <p className="mt-1 text-sm text-red-600">{errors.numberOfPeople}</p>
          )}
          {tour.availableSeats && (
            <p className="mt-1 text-sm text-gray-500">
              {tour.availableSeats} seats available
            </p>
          )}
        </div>

        {/* Message */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Special Requests (Optional)
          </label>
          <textarea
            value={formData.message}
            onChange={(e) => handleInputChange('message', e.target.value)}
            rows="4"
            className=" text-gray-500 w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            placeholder="Any special requests or dietary requirements..."
          />
        </div>

        {/* Discount Code */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Discount Code
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={discountCode}
              onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
              className="flex-1 px-4 text-gray-500 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter code"
              disabled={discount !== null}
            />
            {discount ? (
              <button
                type="button"
                onClick={() => {
                  setDiscount(null);
                  setDiscountCode('');
                }}
                className="px-4 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors"
              >
                Remove
              </button>
            ) : (
              <button
                type="button"
                onClick={handleApplyDiscount}
                disabled={checkingDiscount || !discountCode.trim()}
                className="px-4 py-3 bg-gray-800 hover:bg-gray-900 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {checkingDiscount ? 'Checking...' : 'Apply'}
              </button>
            )}
          </div>
          {discountError && (
            <p className="mt-1 text-sm text-red-600">{discountError}</p>
          )}
          {discount && (
            <p className="mt-1 text-sm text-green-600">
              ✓ Discount applied: {discount.type === 'percentage' ? `${discount.amount}%` : `$${discount.amount}`} off
            </p>
          )}
        </div>

        {/* Price Summary */}
        <div className="bg-gray-50 rounded-lg p-4 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Price per person:</span>
            <span className="font-medium text-gray-600">${tour.price}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Number of people:</span>
            <span className="font-medium text-gray-600">×{formData.numberOfPeople}</span>
          </div>
          {discount && (
            <>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Subtotal:</span>
                <span className="font-medium">${subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm text-green-600">
                <span>Discount:</span>
                <span className="font-medium">-${savings.toFixed(2)}</span>
              </div>
            </>
          )}
          <div className="border-t pt-2 flex justify-between">
            <span className="font-bold text-gray-900">Total:</span>
            <span className="font-bold text-blue-600 text-xl">
              ${total.toFixed(2)}
            </span>
          </div>
        </div>

        {/* Error Message */}
        {errorMessage && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
            {errorMessage}
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={status === 'loading' || tour.availableSeats === 0}
          className="w-full px-6 py-4 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-semibold rounded-lg transition-colors shadow-lg hover:shadow-xl disabled:cursor-not-allowed flex items-center justify-center"
        >
          {status === 'loading' ? (
            <>
              <svg
                className="animate-spin h-5 w-5 mr-2"
               
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  fill="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                 
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Processing...
            </>
          ) : tour.availableSeats === 0 ? (
            'Sold Out'
          ) : (
            'Confirm Booking'
          )}
        </button>

        <p className="text-xs text-gray-500 text-center">
          By booking, you agree to our Terms & Conditions and Privacy Policy
        </p>
      </form>
    </div>
  );
}